import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../components/ui';
import { useT } from '../i18n';

interface Props {
  children: ReactNode;
  /** Wechselt der Key (z.B. location.pathname), wird der Fehler zurückgesetzt → Navigation heilt selbst. */
  resetKey?: string;
}
interface State {
  error: Error | null;
  info: ErrorInfo | null;
  lastKey?: string;
}

// 12 Stack-Zeilen reichen für eine Triage; lange Bundles / Sourcemaps blähen
// sonst das Details-Element unnötig auf.
const STACK_PREVIEW_LINES = 12;

function truncate(text: string, maxLines: number): string {
  const lines = text.split(/\r?\n/);
  if (lines.length <= maxLines) return text;
  return `${lines.slice(0, maxLines).join('\n')}\n…`;
}

function safeMessage(error: Error | null): string {
  if (!error) return '';
  if (typeof error.message === 'string' && error.message.length > 0) return error.message;
  return error.name || String(error);
}

function safeStack(error: Error | null): string {
  if (!error || typeof error.stack !== 'string') return '';
  return error.stack;
}

function safeComponentStack(info: ErrorInfo | null): string {
  if (!info || typeof info.componentStack !== 'string') return '';
  return info.componentStack;
}

function BoundaryFallback({
  onRetry,
  onReload,
}: {
  onRetry: () => void;
  onReload: () => void;
}) {
  const { t } = useT();
  return (
    <main
      role="alert"
      style={{ padding: '32px 20px', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}
    >
      <h1 style={{ marginBottom: 12 }}>{t('common.error')}</h1>
      <p style={{ marginBottom: 20, color: 'var(--ff-text-muted)' }}>
        {t('auth.js.connection_error')}
      </p>
      <div
        style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <Button onClick={onRetry}>{t('chat.retry')}</Button>
        <Button variant="secondary" onClick={onReload}>
          {t('common.reload')}
        </Button>
      </div>
      <details
        style={{
          textAlign: 'start',
          background: 'rgba(var(--ff-bg-primary-rgb), 0.55)',
          border: '1px solid var(--ff-border)',
          borderRadius: 'var(--ff-radius)',
          padding: '10px 12px',
          fontSize: '0.82rem',
          color: 'var(--ff-text-secondary)',
        }}
      >
        <summary style={{ cursor: 'pointer', color: 'var(--ff-gold)' }}>
          {t('common.error_details')}
        </summary>
        <ErrorDetails />
      </details>
    </main>
  );
}

function ErrorDetails() {
  // Lokale Re-Render-Quelle: wir lesen den State aus dem Klassen-Renderpfad
  // über einen window-Cache (siehe render() unten). Das vermeidet einen
  // separaten Provider und hält die Boundary-API klein.
  const cached = readLastBoundaryError();
  if (!cached) {
    return (
      <p style={{ margin: '8px 0 0', color: 'var(--ff-text-muted)' }}>
        (no details available)
      </p>
    );
  }
  const stackText = safeStack(cached.error) || safeComponentStack(cached.info);
  return (
    <pre
      style={{
        margin: '8px 0 0',
        padding: '8px 10px',
        background: 'rgba(0,0,0,0.35)',
        borderRadius: 'var(--ff-radius)',
        overflow: 'auto',
        maxBlockSize: 240,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '0.78rem',
        lineHeight: 1.4,
        color: 'var(--ff-text-primary)',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      <code>{safeMessage(cached.error)}</code>
      {stackText ? (
        <>
          {'\n\n'}
          <code>{truncate(stackText, STACK_PREVIEW_LINES)}</code>
        </>
      ) : null}
    </pre>
  );
}

// Kleiner Modul-Cache, damit ErrorDetails ohne extra Context an den letzten
// Fehler kommt. Wird bei jedem componentDidCatch überschrieben.
let LAST_ERROR: { error: Error; info: ErrorInfo | null } | null = null;
function readLastBoundaryError() {
  return LAST_ERROR;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, lastKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.lastKey) {
      // Navigation heilt: Cache leeren, damit die Details nicht einen
      // veralteten Fehler zeigen, wenn die Boundary später nochmal zuschlägt.
      LAST_ERROR = null;
      return { error: null, info: null, lastKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Bewusst KEIN automatischer reload — ein harter Reload fühlt sich wie
    // ein Crash an. Stattdessen: echte Ursache loggen, damit adb logcat /
    // Safari Web Inspector (Capacitor-Wrapper) die Original-Meldung zeigen.
    // eslint-disable-next-line no-console
    console.error('[LorathRouteError]', error, info.componentStack);
    LAST_ERROR = { error, info };
    this.setState({ info });
  }

  private reset = () => {
    LAST_ERROR = null;
    this.setState({ error: null, info: null });
  };

  // Harter Reload ist die zuverlässigste Heilung bei stale Chunk-Hashes
  // (siehe routes.tsx lazyWithReload): der Browser holt eine frische
  // index.html mit aktuellen Hashes für ALLE Routen.
  private hardReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error) {
      return <BoundaryFallback onRetry={this.reset} onReload={this.hardReload} />;
    }
    return this.props.children;
  }
}
